import React, { useState, useEffect, useRef } from 'react';
import { Typography, Button, TextField, IconButton } from '@mui/material';
import { ECommerceActions } from '../../../../Services/_actions/ECommerce/ecommerce.action';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  botFlowDataAtom,
  flowValidationAtom,
  productFiltersAtom,
} from '../../../../Services/_states';
import SearchableDropdown from '../../Common/SearchableDropdown/SearchableDropdown.tsx';
import { FlowBuilderActions } from '../../../../Services/_actions/index.js';
import { Close } from '@mui/icons-material';
import { Paper } from '@mui/material';
import RenderTextArea from './RenderTextArea.tsx';

interface Product {
  catalog: string;
  name: string;
  image: string;
}
interface SuggetionObj { label: string, value: string, paramType: string };


const ProductNode = ({ data }: any) => {
  const ecommerceActions = ECommerceActions();
  const actions = FlowBuilderActions();
  const botFlowData: any = useRecoilValue(botFlowDataAtom);
  const flowValidationObj = useRecoilValue(flowValidationAtom);
  const [catalogs, setCatalogs] = useState < string[] > ([]); // Mock catalog data
  const [products, setProducts] = useState < Product[] > ([]); // Correct type for product data
  const [productFilter, setProductFilter] = useState({ page: 1, search: '', bot_number: "", catalog_id: "", rowsPerPage: 10 });
  const [availableProducts, setAvailableProducts] = useState < Product[] > ([]);
  const [selectedProducts, setSelectedProducts] = useState < Array < Object >> ([]); // Manage multiple selected products
  const [product, setProduct] = useState([]);
  const [bodyText, setBodyText] = useState(data?.body || '');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState < string[] > (['Option 1', 'Option 2', 'Option 3']);
  const [filteredSuggestions, setFilteredSuggestions] = useState < string[] > ([]);
  const [cursorPosition, setCursorPosition] = useState < number | null > (null);
  const [sections, setSections] = useState([{ title: '', product_items: [] }]);

  const textareaRef = useRef < HTMLTextAreaElement > (null);

  // Update the bodyText state when the data.body prop changes
  useEffect(() => {
    if (data.body !== bodyText) {
      setBodyText(data.body || '');
    }
  }, [data.body]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12}px`;
    }
  }, [bodyText]);

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBodyText(value);

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);

    // Detect `{{@` pattern
    const match = textBeforeCursor.match(/{{@(\w*)$/);
    if (match) {
      // const query = match[1]; // Get the text after `{{@`
      // setFilteredSuggestions(suggestions.filter((s) =>s.label.toLowerCase().includes(query.toLowerCase())));
      setShowSuggestions(true);
      setCursorPosition(cursorPos);
    } else {
      setShowSuggestions(false);
    }

    // Update `params` by finding all valid `{{label}}` entries in the text
    const matches = value.match(/{{(.*?)}}/g); // Match all `{{label}}`
    const labelsInBody = matches
      ? matches.map((match) => match.replace(/{{|}}/g, ""))
      : [];

    // Categorize params by `paramType`
    const categorizedParams = {
      global: [],
      result: [],
      webform: [],
    };

    data.parentResults.forEach((result: SuggetionObj) => {
      if (labelsInBody.includes(result.label)) {
        if (result.paramType === "global") {
          categorizedParams.global.push(result.value);
        } else if (result.paramType === "result") {
          categorizedParams.result.push(result.value);
        } else if (result.paramType === "webform") {
          categorizedParams.webform.push(result.value);
        }
      }
    });

    // Update parent data
    data.onChange(data.id, { body: value, params: categorizedParams });

    // Adjust the textarea height to fit content
    // if (textareaRef.current) {
    //   textareaRef.current.style.height = "auto"; // Reset height
    //   textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 12
    //     }px`; // Set height to content
    // }
  };



  const handleSuggestionClick = (suggestion: SuggetionObj) => {
    if (cursorPosition !== null) {
      const textBeforeCursor = bodyText.slice(0, cursorPosition);
      const textAfterCursor = bodyText.slice(cursorPosition);
      const updatedText =
        textBeforeCursor.replace(/{{@\w*$/, `{{${suggestion.label}}}`) +
        textAfterCursor;

      setBodyText(updatedText);
      setShowSuggestions(false);

      // Initialize parameters if not present
      const updatedParams = {
        global: data.params?.global || [],
        result: data.params?.result || [],
        webform: data.params?.webform || [],
      };

      // Add the value to the correct array based on paramType
      if (suggestion.paramType === "global") {
        updatedParams.global = [...updatedParams.global, suggestion.value];
      } else if (suggestion.paramType === "result") {
        updatedParams.result = [...updatedParams.result, suggestion.value];
      } else if (suggestion.paramType === "webform") {
        updatedParams.webform = [...updatedParams.webform, suggestion.value];
      }

      // Update parent data
      data.onChange(data.id, { body: updatedText, params: updatedParams });

    }
  };

  const getCatalogList = async () => {
    if (!botFlowData?.phone_number_id) return;
    const res = await actions.getBotBuilderCatalogList(botFlowData?.phone_number_id);
    if (res && res.data) {
      setCatalogs(res.data);
      data.onChange(data.id, { catalog: res?.data[0] || {} });
      setProductFilter({
        ...productFilter,
        catalog_id: res?.data[0]?.id,
        bot_number: botFlowData?.phone_number_id,
      });
      getProductItemList({
        ...productFilter,
        catalog_id: res?.data[0]?.id,
        bot_number: botFlowData?.phone_number_id,
      });
    }
  };

  const getProductItemList = async (data: any) => {
    let res = await actions.getBotBuilderProductList(data);
    if (res && res?.length > 0 && res[0] && res[0]?.length > 0) {
      setProduct(res[0]);
    }
  };

  const onSelectCatalogue = (e: any) => {
    data.onChange(data.id, { catalog: catalogs[0] || {} });
    setProductFilter({
      ...productFilter,
      catalog_id: catalogs[0]?.id,
      bot_number: botFlowData?.phone_number_id,
    });
    getProductItemList({
      ...productFilter,
      catalog_id: catalogs[0]?.id,
      bot_number: botFlowData?.phone_number_id,
    });
  };

  const onSearchProduct = (search: string) => {
    getProductItemList({ ...productFilter, search: search });
  };

  const onAddSection = () => {
    setSections([...sections, { title: '', product_items: [] }]);
  };

  const onRemoveSection = (index: number) => {
    const updatedSections = sections.filter((_, i) => i !== index);
    setSections(updatedSections);
    data.onChange(data.id, { sections: updatedSections });
  };

  const onSectionTitleChange = (index: number, value: string) => {
    const updatedSections = sections.map((section, i) => (
      i === index ? { ...section, title: value } : section
    ));
    setSections(updatedSections);
    data.onChange(data.id, { sections: updatedSections });
  };

  const onSelectProductForSection = (index: number, value: any[]) => {
    const updatedSections = sections?.map((section, i) => (
      i === index ? { ...section, product_items: value } : section
    ));
    setSections(updatedSections);
    data.onChange(data.id, { sections: updatedSections });
  };


  useEffect(() => {
    getCatalogList();
  }, [botFlowData]);

  useEffect(() => {
    if (data?.sections)
      setSections(data?.sections);
  }, [])

  return (
    <div>
      {/* Catalog Dropdown */}
      <div style={{ marginBottom: '10px' }}>
        <label>Catalog:</label>
        <select
          value={data?.catalog?.id || ''}
          onChange={onSelectCatalogue}
          style={{
            width: '100%',
            padding: '5px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: '#f5f5f5',
          }}
          disabled
        >
          <option value="">Select Catalog</option>
          {catalogs.map((catalog) => (
            <option key={catalog.id} value={catalog.id}>
              {catalog.name}
            </option>
          ))}
        </select>
      </div>

      <RenderTextArea
        data={data}
      />
      {flowValidationObj[data?.id]?.body && (
        <Typography variant="body2" color="error" style={{ fontSize: '12px' }}>
          {flowValidationObj[data?.id]?.body}
        </Typography>
      )}

      {/* {showSuggestions && data?.parentResults.length > 0 && (
        <div
          style={{
            position: 'absolute',
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: 4,
            zIndex: 1000,
            width: '100%',
          }}
        >
          {data?.parentResults?.map((suggestion: SuggetionObj, index: any) => (
            <div
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: '5px 10px',
                cursor: 'pointer',
                backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#fff',
              }}
            >
              {suggestion?.label}
            </div>
          ))}
        </div>
      )} */}

      {/* Product List */}
      {sections?.map((section, index) => (
        <Paper key={index} elevation={3} style={{ padding: 16, marginBottom: 16, position: 'relative' }}>
          <Typography variant="subtitle1" style={{ marginBottom: 8 }}>Section Title:</Typography>
          <TextField
            fullWidth
            size="small"
            margin="dense"
            variant="outlined"
            value={section?.title || ''}
            onChange={(e) => onSectionTitleChange(index, e.target.value)}
            placeholder="Enter section title"
            style={{ marginBottom: 16 }}
          />
          <SearchableDropdown
            label="Select Products for Section"
            options={product?.map((p: any) => ({
              ...p,
              label: p?.name,
              value: p?.id,
            }))}
            value={section?.product_items}
            onChange={(value: any) => onSelectProductForSection(index, value)}
            onSearch={onSearchProduct}
            multiSelect={true}
          />

          {sections?.length > 1 &&
            <IconButton
              onClick={() => onRemoveSection(index)}
              style={{ position: 'absolute', top: 16, right: 16 }}
              color="error"
            >
              <Close />
            </IconButton>
          }
        </Paper>
      ))}
      <Typography
        variant="body2"
        style={{
          marginTop: '10px',
          cursor: 'pointer',
          color: '#007bff',
          textDecoration: 'underline',
          fontSize: '14px',
        }}
        onClick={onAddSection}
      >
        + Add Section
      </Typography>
    </div>
  );
};

export default ProductNode;